import {
    Button,
    Card,
    CardActionArea,
    CardActions,
    CardContent,
    CardMedia,
    InputAdornment,
    LinearProgress,
    Pagination,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import SearchIcon from "@mui/icons-material/Search";
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import {PersonaType, Video, VideoAd} from "../../models";
import {getVideoAds, getVideos, linkVideo, linkVideoAd} from "../../api";
import Content from "../../api/content";
import {useSnackbar} from "notistack";
import {useParams} from "react-router-dom";


const itemsPerPage = 15;

type Page =
    | "content"
    | "stims"
    | "stories"
    | "watch"
    | "watchStims"
    | "reel"
    | "reelStims"
    | "video"
    | "videoStims";

interface API {
    content: string;
    stim: string;
}

interface PersonaContentBrowserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onLinkedContent: () => void | Promise<void>;
    pageData: Page;
    currentContent: any[];
    personaType: PersonaType;
    disabled?: boolean;
}

export const PersonaContentBrowserDialog: React.FC<PersonaContentBrowserDialogProps> = ({
                                                                                            open,
                                                                                            onOpenChange,
                                                                                            onLinkedContent,
                                                                                            pageData,
                                                                                            currentContent,
                                                                                            personaType,
                                                                                            disabled = false,
                                                                                        }) => {
    const {id} = useParams<{ id: string }>();
    const {enqueueSnackbar} = useSnackbar();

    const [query, setQuery] = useState("");
    const [type, setType] = useState<"all" | "videos" | "video_ads">("all");
    const [page, setPage] = useState(1);

    const [content, setContent] = useState<any[] | null>(null);
    const [error, setError] = useState<any | null>(null);
    const [linkingContentId, setLinkingContentId] = useState<number | null>(null);

    const getType = ():API => {
        let api: API = {
            content: "content",
            stim: "stim"
        }

        switch (personaType) {
            case "reel":
                api.content = "reel"
                api.stim = "reelStim"
                break
            case "gmail":
                api.content = "gmail"
                api.stim = "stim"
                break
            case "music":
                api.content = "music"
                api.stim = "musicStim"
                break
            case "article":
                api.content = "article"
                api.stim = "stim"
                break
            case "streamer":
                api.content = "streamer"
                api.stim = "streamerStim"
                break
            default:
                // Do nothing
                break
        }

        return api
    }

    const searchContent = useMemo(() => {
        return content?.filter((item) => {
            return (item.title ?? "").toLowerCase().includes(query.toLowerCase());
        });
    }, [query, content]);

    const paginatedContent = useMemo(() => searchContent?.slice((page - 1) * itemsPerPage, page * itemsPerPage), [searchContent, page]);

    const loading = useMemo(() => !(content) && !error, [content, error]);

    const fetchData = useCallback(async () => {
        setError(null);

        let api = getType()
        let data = []

        try {
            switch (pageData) {
                case "content":
                    if (personaType === "music") {
                        data = await Content.getAllPlaylists()
                    } else {
                        data = await Content.getAllContent(api.content)
                    }
                    break
                case "stims":
                    data = await Content.getAllContent(api.stim)
                    break
                case "stories":
                    data = await Content.getAllContent("story")
                    break
                case "watch":
                    data = await Content.getAllContent("watch")
                    break
                case "watchStims":
                    data = await Content.getAllContent("watchStim")
                    break
                case "reel":
                    data = await Content.getAllContent("reel")
                    break
                case "reelStims":
                    data = await Content.getAllContent("reelStim")
                    break
                case "video":
                    data = await getVideos()
                    break
                case "videoStims":
                    data = await getVideoAds()
                    break
                default:
                    enqueueSnackbar("Message Devs Immediately Error Code: P2003", {variant: "error"});
                    break
            }
            setContent(data)
        } catch (error) {
            setError(error);
        }
    }, []);

    useEffect(() => {
        setPage(1);
    }, [query, type]);

    // Fetch data on mount
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    function isContentInFeed(content: Video | VideoAd) {
        return currentContent.some((item) => item.id === content.id);
    }

    async function linkContent(content: Video | VideoAd) {
        if (disabled || linkingContentId) return;

        enqueueSnackbar("Linking content...", {variant: "info"});
        setLinkingContentId(content.id);

        let api = getType()

        let order = ([...currentContent].sort((a, b) => a.order - b.order).at(-1)?.order ?? 0) + 1;

        try {
            switch (pageData) {
                case "content":
                    await Content.linkContentNew(id, content.id, order, api.content)
                    break
                case "stims":
                    await Content.linkContentNew(id, content.id, order, api.stim)
                    break
                case "stories":
                    await Content.linkContentNew(id, content.id, order, "story")
                    break
                case "watch":
                    await Content.linkContentNew(id, content.id, order, "watch")
                    break
                case "watchStims":
                    await Content.linkContentNew(id, content.id, order, "watchStim")
                    break
                case "reel":
                    await Content.linkContentNew(id, content.id, order, "reel")
                    break
                case "reelStims":
                    await Content.linkContentNew(id, content.id, order, "reelStim")
                    break
                case "video":
                    await linkVideo(Number(id), content.id, {
                        order: order,
                        locked: false,
                    });
                    break
                case "videoStims":
                    await linkVideoAd(Number(id), content.id, {
                        order: order,
                        locked: false,
                    });
                    break
                default:
                    enqueueSnackbar("Message Devs Immediately Error Code: P2000", {variant: "error"});
                    break
            }
            await onLinkedContent()
            enqueueSnackbar("Content linked", {variant: "success"});
        } catch (e) {
            enqueueSnackbar("Failed to link content", {variant: "error"});
            console.error(e);
        } finally {
            setLinkingContentId(null);
        }
    }

    return (
        <div className={(open ? 'user-box' : 'user-box hide')}>
            <div className="link-container">
                <div style={{display: "flex", padding: "20px"}}>
                    <TextField
                        label="Search"
                        variant="outlined"
                        style={{marginRight: "10px"}}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon/>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <ToggleButtonGroup
                        color="primary"
                        value={type}
                        exclusive
                        onChange={(_, value) => {
                            if (!value) return;
                            setType(value);
                        }}
                        style={{marginLeft: "10px"}}
                    >
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="videos">Video</ToggleButton>
                        <ToggleButton value="video_ads">Video Ads</ToggleButton>
                    </ToggleButtonGroup>
                </div>

                <div className="profiles-container" style={{justifyContent: "left", marginLeft: 0, width: "100%"}}>
                    {loading && <div style={{width: "50%", margin: "auto"}}>
                        <LinearProgress/>
                    </div>}

                    {!loading && paginatedContent?.map((item) => (
                        <Card key={item.id}
                              style={{
                                  width: "340px",
                                  marginBottom: "20px",
                                  backgroundColor: "#424242",
                                  marginLeft: "17px",
                                  position: "relative"
                              }}>
                            {item.status === "src_issue" && (
                                <div className="src-warning">
                                    <Tooltip title={"Source Error"}><GppMaybeIcon color="error"/></Tooltip>
                                </div>
                            )}
                            <CardActionArea>
                                {(() => {
                                    const src = personaType === "video" ? item.contentUrl : item.src;

                                    return src ? (
                                        <CardMedia
                                            className={isContentInFeed(item) ? "onFeed" : ""}
                                            component={src.split(".").pop() === "mp4" ? "video" : "div"}
                                            style={{ height: "140px" }}
                                            image={src}
                                            title={item.title}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                color: "#ffffff85",
                                                fontWeight: 700,
                                                fontSize: "30px",
                                                height: "140px"
                                            }}
                                        >
                                            {isContentInFeed(item) ? "IN FEED" : "WHERE'S THE LAMB SAUCE"}
                                        </div>
                                    );
                                })()}
                                <CardContent style={{backgroundColor: "#515151", height: "80px"}}>
                                    <div style={{
                                        color: "rgba(255, 255, 255, 0.7)",
                                        fontSize: "18px"
                                    }}>{item.title}</div>
                                </CardContent>
                            </CardActionArea>
                            <CardActions>
                                <Button disabled={disabled || linkingContentId !== null || isContentInFeed(item)} size="small" variant="contained"
                                        color="secondary" onClick={() => linkContent(item)}>
                                    ADD
                                </Button>
                            </CardActions>
                        </Card>
                    ))}
                </div>

                <div style={{
                    padding: "5px",
                    alignSelf: "center",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    justifySelf: "end"
                }}>
                    {searchContent && <Pagination count={Math.ceil(searchContent.length / itemsPerPage)} page={page}
                                                  variant="outlined" onChange={(_, page) => setPage(page)}/>}
                    <div style={{display: "flex", justifyContent: "space-evenly"}}>
                        <Button variant="contained" color="primary" onClick={() => onOpenChange(false)}>Close</Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
